/**
 * AuthContext — the single source of truth for who is signed in.
 *
 * Identity comes from Google via Emergent Auth. The session lives in an
 * httpOnly cookie, so JavaScript can never read it: the only way to know
 * whether we are authenticated is to ask the server (`GET /api/auth/me`).
 *
 * `user.status` matters as much as `user` itself. A brand-new sign-in is
 * `pending` until an admin approves it, so a user can be authenticated and
 * still be allowed to do nothing.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

/** Fired by the axios interceptor when the server rejects our session. */
export const SESSION_EXPIRED_EVENT = "app-session-expired";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data?.user || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from the OAuth redirect: AuthCallback must exchange the
    // session_id and set the cookie first, otherwise /auth/me 401s and we
    // would bounce the user back to the login screen.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Even if the call fails the local session is unusable to us.
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    isActive: user?.status === "active",
    isAdmin: user?.role === "admin" && user?.status === "active",
    setUser,
    refresh: checkAuth,
    logout,
  }), [user, loading, checkAuth, logout]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
